import { Children, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";

const KEY = "cp-ops-split";

function readRatio() {
  const raw = Number(localStorage.getItem(KEY));
  return Number.isFinite(raw) && raw > 0.3 && raw < 0.7 ? raw : 0.5;
}

export function SplitPane({ children }: { children: ReactNode }) {
  const [ratio, setRatio] = useState(0.5);
  const [stacked, setStacked] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [left, right] = Children.toArray(children);

  useEffect(() => {
    setRatio(readRatio());
    const mq = window.matchMedia("(max-width: 960px)");
    const apply = () => setStacked(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  function onPointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    const move = (ev: PointerEvent) => {
      const rect = box.current?.getBoundingClientRect();
      if (!rect || rect.width < 1) return;
      const next = Math.min(0.68, Math.max(0.32, (ev.clientX - rect.left) / rect.width));
      setRatio(next);
      localStorage.setItem(KEY, String(next));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  return (
    <div
      className="ops-split"
      ref={box}
      style={stacked ? undefined : { gridTemplateColumns: `minmax(0, ${ratio}fr) 14px minmax(0, ${1 - ratio}fr)` }}
    >
      {left}
      <button
        aria-label="Resize list and preview"
        className="ops-split__handle"
        onDoubleClick={() => {
          setRatio(0.5);
          localStorage.setItem(KEY, "0.5");
        }}
        onPointerDown={onPointerDown}
        type="button"
      />
      {right}
    </div>
  );
}
